/**
 * Dashboard Targets Modal
 *
 * Modal for configuring daily dashboard targets (pick lines, receive lines, shipments).
 * Targets are persisted via the API to SystemSetting table.
 *
 * @author BizClick WMS
 * @version 1.0.0
 */

import React, { useState, useEffect } from 'react';
import { X, Target, Save, Loader2 } from 'lucide-react';
import {
  fetchDashboardTargets,
  updateDashboardTargets,
  DashboardTargets,
} from '../../../../../services/dashboardService';

interface DashboardTargetsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DashboardTargetsModal: React.FC<DashboardTargetsModalProps> = ({ isOpen, onClose }) => {
  const [targets, setTargets] = useState<DashboardTargets>({
    pickLinesDaily: 750,
    receiveLinesDaily: 300,
    shipmentsDaily: 50,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadTargets();
      setSaved(false);
    }
  }, [isOpen]);

  const loadTargets = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchDashboardTargets();
      setTargets(data);
    } catch {
      setError('Kunne ikke hente målsettinger');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await updateDashboardTargets(targets);
      setSaved(true);
      setTimeout(() => onClose(), 1000);
    } catch {
      setError('Kunne ikke lagre målsettinger');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-[100]" onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center z-[110] p-4">
        <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-2xl w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-700">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-400" />
              <h2 className="text-lg font-semibold text-white">Daglige malsettinger</h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-400">
                  Sett daglige mal for dashboard-widgets. Disse brukes i fremgangsvisninger og KPI-er.
                </p>

                {/* Pick Lines */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Plukkelinjer per dag
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={targets.pickLinesDaily}
                    onChange={(e) =>
                      setTargets({ ...targets, pickLinesDaily: parseInt(e.target.value) || 0 })
                    }
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 transition-colors"
                  />
                </div>

                {/* Receive Lines */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Mottakslinjer per dag
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={targets.receiveLinesDaily}
                    onChange={(e) =>
                      setTargets({ ...targets, receiveLinesDaily: parseInt(e.target.value) || 0 })
                    }
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 transition-colors"
                  />
                </div>

                {/* Shipments */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Forsendelser per dag
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={targets.shipmentsDaily}
                    onChange={(e) =>
                      setTargets({ ...targets, shipmentsDaily: parseInt(e.target.value) || 0 })
                    }
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 transition-colors"
                  />
                </div>

                {error && (
                  <p className="text-sm text-red-400">{error}</p>
                )}

                {saved && (
                  <p className="text-sm text-green-400">Malsettinger lagret!</p>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
            >
              Avbryt
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || isLoading}
              className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg transition-colors"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Lagre
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default DashboardTargetsModal;
